import { Row, Col, Button, Table, Spin } from 'antd'
import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { stepperMoving } from '../../stores/actions';
import { postApi } from '../../../../shared/src/apis';
import {
    LoadingOutlined, CheckCircleOutlined, CheckCircleFilled, WarningOutlined, UndoOutlined,
    SafetyCertificateOutlined, FileProtectOutlined, ReloadOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { exportToCSV, DataTable } from '@operations/shared';
import { createBulkHousingIqamaApi } from '../../utils/apisConsts';
import helper from '../../utils/helper';
import { InvalidHousingIqamaRow, toHousingIqamaPayload } from './validateRows';
import {
    HousingIqamaApiError,
    buildFailedHousingIqamaResult,
} from './parseApiErrors';

export type { HousingIqamaApiError };

interface IRootState {
    [key: string]: any;
}

const failedColumns: any = [
    { title: 'Phone', dataIndex: 'phone' },
    { title: 'Service ID', dataIndex: 'serviceID' },
    { title: 'Amount', dataIndex: 'amount', render: (value: any) => helper.amountFormatter(value) },
    { title: 'Installments', dataIndex: 'numberOfInstallments' },
    {
        title: 'Error',
        dataIndex: 'error',
        render: (value: string) => (
            <span className="housing-iqama-review__fail-reason">{value}</span>
        ),
    },
];

const invalidColumns: any = [
    { title: 'Row', dataIndex: 'rowNumber', width: 80 },
    { title: 'Phone', dataIndex: 'phone' },
    { title: 'Service ID', dataIndex: 'serviceID' },
    { title: 'Amount', dataIndex: 'amount', render: (value: any) => helper.amountFormatter(value) },
    { title: 'Installments', dataIndex: 'numberOfInstallments' },
    {
        title: 'Errors',
        dataIndex: 'errors',
        render: (errors: string[]) => (
            <ul className="housing-iqama-review__error-list">
                {Array.isArray(errors) && errors.map((error, key) => (
                    <li key={key}>{error}</li>
                ))}
            </ul>
        ),
    },
];

export default function Confirmation() {
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [reason, setReasons] = useState<any[]>([]);
    const [errorMode, setErrorMode] = useState(false);
    const [numOfFailedRequests, setNumOfFailedRequests] = useState(0);
    const [rejectedRequests, setRejectedRequests] = useState<any[]>([]);
    const [resErrorContent, setResErrorContent] = useState<string | undefined>(undefined);
    const [pagination, setPagination] = useState({});

    const dispatch = useDispatch();
    const dataSheet: any = useSelector<IRootState>(state => state.actionsReducer.dataSheet);
    const sheetData: any[] = useMemo(() => dataSheet?.sheetData || [], [dataSheet]);
    const invalidRows: InvalidHousingIqamaRow[] = useMemo(() => dataSheet?.invalidRows || [], [dataSheet]);
    const hasInvalidRows = invalidRows.length > 0;
    const tableColumns = helper.generateTableColumns(sheetData, 160).map((column) =>
        column.dataIndex === 'amount' ? { ...column, render: (value: any) => helper.amountFormatter(value) } : column
    );

    const submitRequests = useCallback(async () => {
        setSubmitting(true)
        setErrorMode(false)
        try {
            const payload = toHousingIqamaPayload(sheetData);
            const apiData: any = await dispatch(postApi(createBulkHousingIqamaApi, payload));
            const res = apiData?.payload;
            const status = res?.status;
            const apiErrors: HousingIqamaApiError[] = Array.isArray(res?.errors) ? res.errors : [];

            if (status === 201) {
                setReasons([]);
                setRejectedRequests([]);
                setNumOfFailedRequests(0);
                setResErrorContent(res?.message);
                setSubmitted(true);
                setSubmitting(false);
                return;
            }

            const { failedRows, rejectedRequests, count } = buildFailedHousingIqamaResult(apiErrors);
            if ((status === 400 || count > 0) && count > 0) {
                setNumOfFailedRequests(count);
                setReasons(failedRows);
                setRejectedRequests(rejectedRequests);
                setResErrorContent(res?.message);
                setSubmitted(true);
                setSubmitting(false);
                return;
            }

            setSubmitting(false);
            setSubmitted(true);
            setErrorMode(true);
            setResErrorContent(res?.message);
        } catch (error) {
            setSubmitting(false);
            setSubmitted(true);
            setErrorMode(true)
        }
    }, [dispatch, sheetData]);

    const goToUpload = () => dispatch(stepperMoving(0));

    if (submitting) {
        return (
            <div className="approveUsers">
                <div className="housing-iqama-loader">
                    <div className="housing-iqama-loader__ring">
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#0d5257' }} spin />} />
                    </div>
                    <h3>Placing Housing/Iqama requests</h3>
                    <p>We’re sending your sheet to the server. Keep this window open.</p>
                    <span className="housing-iqama-loader__hint">This can take a few minutes</span>
                </div>
            </div>
        )
    }

    if (submitted) {
        if (errorMode) {
            return (
                <div className="approveUsers">
                    <div className="housing-iqama-result">
                        <div className="housing-iqama-result__visual">
                            <span className="housing-iqama-result__icon"><UndoOutlined /></span>
                            <span className="housing-iqama-result__badge">Rolled back</span>
                        </div>
                        <h3>Nothing was placed</h3>
                        <p className="housing-iqama-result__lead">
                            The Housing/Iqama batch didn’t go through. None of the {sheetData.length} {sheetData.length === 1 ? 'request was' : 'requests were'} created.
                        </p>
                        {resErrorContent && (
                            <div className="housing-iqama-result__quote">{resErrorContent}</div>
                        )}
                        <ul className="housing-iqama-result__facts">
                            <li><SafetyCertificateOutlined /> No requests were saved on the server</li>
                            <li><FileProtectOutlined /> Your original sheet is unchanged</li>
                            <li><ReloadOutlined /> Fix the file, then re-upload and try again</li>
                        </ul>
                        <Button type="primary" className="housing-iqama-guide__ok" onClick={goToUpload}>
                            Re-upload file
                        </Button>
                    </div>
                </div>
            )
        }

        if (reason.length > 0) {
            return (
                <div className="approveUsers housing-iqama-review">
                    <div className="housing-iqama-review__banner housing-iqama-review__banner--error">
                        <WarningOutlined />
                        <div>
                            <strong>{resErrorContent || 'Validation error'}</strong>
                            <p>
                                {numOfFailedRequests} {numOfFailedRequests === 1 ? 'row failed' : 'rows failed'} and nothing was created.
                                Download the failed rows, fix them, and re-upload.
                            </p>
                        </div>
                    </div>
                    <Row style={{ marginBottom: 16 }} align="middle">
                        <Col span={16} style={{ textAlign: 'left' }}>
                            <b>Failed rows:</b> {numOfFailedRequests}
                        </Col>
                        <Col span={8} style={{ textAlign: 'right' }}>
                            <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(rejectedRequests, 'Housing Iqama errors')}>
                                Download failed rows
                            </Button>
                        </Col>
                    </Row>
                    <Table
                        columns={failedColumns}
                        dataSource={reason}
                        pagination={false}
                        bordered
                        scroll={{ x: 900 }}
                    />
                    <div className="housing-iqama-review__actions">
                        <Button className='reUpload' onClick={goToUpload}>Re-upload file</Button>
                    </div>
                </div>
            )
        }

        return (
            <div className="approveUsers">
                <div className="housing-iqama-result housing-iqama-result--ok">
                    <div className="housing-iqama-result__visual">
                        <span className="housing-iqama-result__icon"><CheckCircleFilled /></span>
                        <span className="housing-iqama-result__badge">Created</span>
                    </div>
                    <h3>Requests placed</h3>
                    <p className="housing-iqama-result__lead">
                        All {sheetData.length} Housing/Iqama {sheetData.length === 1 ? 'request was' : 'requests were'} created successfully.
                    </p>
                    <div className="housing-iqama-result__stat">
                        <strong>{sheetData.length}</strong>
                        <span>{sheetData.length === 1 ? 'request created' : 'requests created'}</span>
                    </div>
                    <ul className="housing-iqama-result__facts">
                        <li><SafetyCertificateOutlined /> They are saved and ready in the system</li>
                        <li><FileProtectOutlined /> You can close this action or upload another file</li>
                    </ul>
                    <Button type="primary" className="housing-iqama-guide__ok" onClick={goToUpload}>
                        Upload another file
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="approveUsers housing-iqama-review">
            {hasInvalidRows ? (
                <>
                    <div className="housing-iqama-review__banner housing-iqama-review__banner--error">
                        <WarningOutlined />
                        <div>
                            <strong>{invalidRows.length} {invalidRows.length === 1 ? 'row has' : 'rows have'} missing or invalid data.</strong>
                            <p>Fix these rows in the sheet and re-upload. Nothing will be submitted until every row is valid.</p>
                        </div>
                    </div>
                    <Table
                        columns={invalidColumns}
                        dataSource={invalidRows.map((row, index) => ({ ...row, key: index }))}
                        pagination={false}
                        bordered
                        scroll={{ x: 900 }}
                    />
                </>
            ) : (
                <>
                    <div className="housing-iqama-review__banner housing-iqama-review__banner--ok">
                        <CheckCircleOutlined />
                        <div>
                            <strong>All {sheetData.length} {sheetData.length === 1 ? 'row looks' : 'rows look'} valid.</strong>
                            <p>Review the data below, then confirm to create the Housing/Iqama requests.</p>
                        </div>
                    </div>
                    <div className="housing-iqama-review__count">
                        <b>Uploaded rows:</b> {sheetData.length}
                    </div>
                    <DataTable
                        columns={tableColumns}
                        pagination={pagination}
                        rows={sheetData.map((row, index) => ({ ...row, key: index }))}
                        handleTableChange={setPagination}
                        loading={false}
                        scrolling={{ x: 900 }}
                    />
                </>
            )}
            <div className="housing-iqama-review__actions">
                <Button className='reUpload' onClick={goToUpload}>Re-upload file</Button>
                {!hasInvalidRows && (
                    <Button type="primary" className="housing-iqama-guide__ok" onClick={submitRequests}>
                        Confirm & Submit
                    </Button>
                )}
            </div>
        </div>
    )
}
